'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from 'react';
import Link from 'next/link';

// Utils
import { routes } from '@routes';

// UI
import { ListingCard, type ListingPartial } from '@components/listing-card';
import { Typography } from '@components/typography';
import { CarouselNavButton } from './carousel-nav-button';

// Hooks
import { useCarousel, useFavoritedListingIds } from '@hooks';
interface StaysSectionProps {
  title: string;
  listings: ListingPartial[];
  itemsPerView?: number;
  /** Optional extra classes for the section heading (e.g. light text on a dark band). */
  titleClassName?: string;
  /** Optional small descriptor rendered beneath the title. */
  subtitle?: string;
  /** Optional extra classes for the subtitle. */
  subtitleClassName?: string;
  /** Optional decorative emoji/glyph rendered before the title. */
  emoji?: string;
  /** Optional classes applied to each card container (e.g. a white surface on a dark band). */
  cardClassName?: string;
  /** Optional "view more" link target rendered beneath the carousel. */
  ctaHref?: string;
  /** Label for the optional "view more" link (required for the link to render). */
  ctaLabel?: string;
  /** Optional extra classes for the "view more" link (e.g. light text on a dark band). */
  ctaClassName?: string;
}
export const StaysSection: React.FC<StaysSectionProps> = t0 => {
  const $ = _c(92);
  const {
    title,
    listings,
    titleClassName,
    subtitle,
    subtitleClassName,
    emoji,
    cardClassName,
    ctaHref,
    ctaLabel,
    ctaClassName
  } = t0;
  const {
    isFavorited,
    setFavorited
  } = useFavoritedListingIds();
  const [responsiveItemsPerView, setResponsiveItemsPerView] = useState(2);
  const [isDesktop, setIsDesktop] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const updateItemsPerView = () => {
        if (window.innerWidth >= 1024) {
          setResponsiveItemsPerView(4);
          setIsDesktop(true);
        } else {
          if (window.innerWidth >= 768) {
            setResponsiveItemsPerView(3);
            setIsDesktop(true);
          } else {
            if (window.innerWidth >= 640) {
              setResponsiveItemsPerView(2);
              setIsDesktop(true);
            } else {
              setResponsiveItemsPerView(2);
              setIsDesktop(false);
            }
          }
        }
      };
      updateItemsPerView();
      window.addEventListener("resize", updateItemsPerView);
      return () => window.removeEventListener("resize", updateItemsPerView);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] !== responsiveItemsPerView) {
    t3 = {
      itemsPerView: responsiveItemsPerView
    };
    $[2] = responsiveItemsPerView;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    currentSlide,
    totalSlides,
    nextSlide,
    prevSlide,
    getVisibleItems
  } = useCarousel(listings.length, t3);
  const showArrows = isDesktop && listings.length > responsiveItemsPerView;
  if (listings.length === 0) {
    return null;
  }
  let t10;
  let t11;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[4] !== cardClassName || $[5] !== currentSlide || $[6] !== emoji || $[7] !== getVisibleItems || $[8] !== isFavorited || $[9] !== listings || $[10] !== nextSlide || $[11] !== prevSlide || $[12] !== setFavorited || $[13] !== showArrows || $[14] !== subtitle || $[15] !== subtitleClassName || $[16] !== title || $[17] !== titleClassName || $[18] !== totalSlides) {
    const visibleListings = getVisibleItems(listings);
    const listingRoute = _temp;
    t11 = "w-full";
    t8 = "max-w-7xl -mx-4 sm:mx-auto px-8";
    let t12;
    if ($[27] !== emoji) {
      t12 = emoji && <span aria-hidden="true" className="text-2xl leading-none sm:text-3xl">{emoji}</span>;
      $[27] = emoji;
      $[28] = t12;
    } else {
      t12 = $[28];
    }
    const t13 = `text-left text-primary-main !text-3xl sm:!text-4xl lg:!text-[2.5rem] ${titleClassName ?? ""}`;
    let t14;
    if ($[29] !== t13 || $[30] !== title) {
      t14 = <Typography variant="title3" component="h2" className={t13}>{title}</Typography>;
      $[29] = t13;
      $[30] = title;
      $[31] = t14;
    } else {
      t14 = $[31];
    }
    let t15;
    if ($[32] !== t12 || $[33] !== t14) {
      t15 = <div className="flex items-center gap-2 sm:gap-3">{t12}{t14}</div>;
      $[32] = t12;
      $[33] = t14;
      $[34] = t15;
    } else {
      t15 = $[34];
    }
    let t16;
    if ($[35] !== subtitle || $[36] !== subtitleClassName) {
      t16 = subtitle && <Typography variant="body2" component="p" className={`text-gray-main ${subtitleClassName ?? ""}`}>{subtitle}</Typography>;
      $[35] = subtitle;
      $[36] = subtitleClassName;
      $[37] = t16;
    } else {
      t16 = $[37];
    }
    let t17;
    if ($[38] !== t15 || $[39] !== t16) {
      t17 = <div className="flex flex-col gap-1">{t15}{t16}</div>;
      $[38] = t15;
      $[39] = t16;
      $[40] = t17;
    } else {
      t17 = $[40];
    }
    let t18;
    if ($[41] !== currentSlide || $[42] !== nextSlide || $[43] !== prevSlide || $[44] !== showArrows || $[45] !== title || $[46] !== totalSlides) {
      t18 = showArrows && <div className="flex flex-row items-center gap-3"><CarouselNavButton direction="prev" disabled={currentSlide === 0} onClick={prevSlide} title={title} /><CarouselNavButton direction="next" disabled={currentSlide === totalSlides - 1} onClick={nextSlide} title={title} /></div>;
      $[41] = currentSlide;
      $[42] = nextSlide;
      $[43] = prevSlide;
      $[44] = showArrows;
      $[45] = title;
      $[46] = totalSlides;
      $[47] = t18;
    } else {
      t18 = $[47];
    }
    if ($[48] !== t17 || $[49] !== t18) {
      t9 = <div className="flex flex-row items-center justify-between mb-4 sm:mb-8">{t17}{t18}</div>;
      $[48] = t17;
      $[49] = t18;
      $[50] = t9;
    } else {
      t9 = $[50];
    }
    let t19;
    if ($[51] !== cardClassName || $[52] !== isFavorited || $[53] !== listings || $[54] !== setFavorited) {
      let t20;
      if ($[56] !== cardClassName || $[57] !== isFavorited || $[58] !== setFavorited) {
        t20 = listing_0 => <div key={listing_0.id} className="flex-shrink-0 w-[280px] snap-start"><ListingCard listing={listing_0} isFavorited={isFavorited(listing_0.id)} onFavoritedSuccess={nowFavorited => setFavorited(listing_0.id, nowFavorited)} altTag={listing_0.name || "Property"} showRoute={listingRoute(listing_0)} accountShowRoute={routes.accounts(listing_0.account.slug)} displayStay={true} isHomePage={true} isHomePageMobile={true} containerClassName={cardClassName} /></div>;
        $[56] = cardClassName;
        $[57] = isFavorited;
        $[58] = setFavorited;
        $[59] = t20;
      } else {
        t20 = $[59];
      }
      t19 = listings.filter(_temp2).map(t20);
      $[51] = cardClassName;
      $[52] = isFavorited;
      $[53] = listings;
      $[54] = setFavorited;
      $[55] = t19;
    } else {
      t19 = $[55];
    }
    let t20;
    if ($[60] !== t19) {
      t20 = <div className="flex gap-4 overflow-x-auto pb-4 px-4 scroll-smooth snap-x snap-mandatory">{t19}</div>;
      $[60] = t19;
      $[61] = t20;
    } else {
      t20 = $[61];
    }
    let t21;
    if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = <div className="absolute top-0 right-0 bottom-4 w-8 bg-gradient-to-l from-white to-transparent pointer-events-none" />;
      $[62] = t21;
    } else {
      t21 = $[62];
    }
    if ($[63] !== t20) {
      t10 = <div className="block sm:hidden pt-2 -mx-4 relative">{t20}{t21}</div>;
      $[63] = t20;
      $[64] = t10;
    } else {
      t10 = $[64];
    }
    t7 = "hidden sm:block relative";
    t6 = "relative";
    t4 = "grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4";
    let t22;
    if ($[65] !== cardClassName || $[66] !== currentSlide || $[67] !== isFavorited || $[68] !== setFavorited) {
      t22 = (listing_1, index) => <ListingCard key={`${listing_1.id}-${currentSlide}-${index}`} listing={listing_1} isFavorited={isFavorited(listing_1.id)} onFavoritedSuccess={nowFavorited_0 => setFavorited(listing_1.id, nowFavorited_0)} altTag={listing_1.name || "Property"} showRoute={listingRoute(listing_1)} accountShowRoute={routes.accounts(listing_1.account.slug)} displayStay={true} isHomePage={true} containerClassName={cardClassName} />;
      $[65] = cardClassName;
      $[66] = currentSlide;
      $[67] = isFavorited;
      $[68] = setFavorited;
      $[69] = t22;
    } else {
      t22 = $[69];
    }
    t5 = visibleListings.filter(_temp3).map(t22);
    $[4] = cardClassName;
    $[5] = currentSlide;
    $[6] = emoji;
    $[7] = getVisibleItems;
    $[8] = isFavorited;
    $[9] = listings;
    $[10] = nextSlide;
    $[11] = prevSlide;
    $[12] = setFavorited;
    $[13] = showArrows;
    $[14] = subtitle;
    $[15] = subtitleClassName;
    $[16] = title;
    $[17] = titleClassName;
    $[18] = totalSlides;
    $[19] = t10;
    $[20] = t11;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
    $[26] = t9;
  } else {
    t10 = $[19];
    t11 = $[20];
    t4 = $[21];
    t5 = $[22];
    t6 = $[23];
    t7 = $[24];
    t8 = $[25];
    t9 = $[26];
  }
  let t12;
  if ($[70] !== t4 || $[71] !== t5) {
    t12 = <div className={t4}>{t5}</div>;
    $[70] = t4;
    $[71] = t5;
    $[72] = t12;
  } else {
    t12 = $[72];
  }
  let t13;
  if ($[73] !== t12 || $[74] !== t6) {
    t13 = <div className={t6}>{t12}</div>;
    $[73] = t12;
    $[74] = t6;
    $[75] = t13;
  } else {
    t13 = $[75];
  }
  let t14;
  if ($[76] !== t13 || $[77] !== t7) {
    t14 = <div className={t7}>{t13}</div>;
    $[76] = t13;
    $[77] = t7;
    $[78] = t14;
  } else {
    t14 = $[78];
  }
  let t15;
  if ($[79] !== ctaClassName || $[80] !== ctaHref || $[81] !== ctaLabel) {
    t15 = ctaHref && ctaLabel && <div className="mt-4 sm:mt-6"><Link href={ctaHref} className={`inline-flex items-center gap-1 text-sm font-semibold text-primary-main underline-offset-4 hover:underline ${ctaClassName ?? ""}`}>{ctaLabel}<span aria-hidden="true">→</span></Link></div>;
    $[79] = ctaClassName;
    $[80] = ctaHref;
    $[81] = ctaLabel;
    $[82] = t15;
  } else {
    t15 = $[82];
  }
  let t16;
  if ($[83] !== t10 || $[84] !== t14 || $[85] !== t15 || $[86] !== t8 || $[87] !== t9) {
    t16 = <div className={t8}>{t9}{t10}{t14}{t15}</div>;
    $[83] = t10;
    $[84] = t14;
    $[85] = t15;
    $[86] = t8;
    $[87] = t9;
    $[88] = t16;
  } else {
    t16 = $[88];
  }
  let t17;
  if ($[89] !== t11 || $[90] !== t16) {
    t17 = <div className={t11}>{t16}</div>;
    $[89] = t11;
    $[90] = t16;
    $[91] = t17;
  } else {
    t17 = $[91];
  }
  return t17;
};
function _temp(listing) {
  return routes.listings({
    slug: listing.slug,
    checkInDate: listing.priceDisplay?.checkInDate,
    checkOutDate: listing.priceDisplay?.checkOutDate
  });
}
function _temp2(l) {
  return l.slug;
}
function _temp3(l_0) {
  return l_0.slug;
}